import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../../../firebase/config'; 
import { collectionGroup, query, where, getDocs } from 'firebase/firestore';
import { getEndosoStatusChip } from '../../../../utils/styles';

/**
 * MovimientosDePago - Solapa interna para DetalleCliente.
 * Historial de cambios de cobro con estética idéntica a EndososTab.
 */
const MovimientosDePago = ({ client }) => {
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showSensitiveData, setShowSensitiveData] = useState({});
  const [filtroPoliza, setFiltroPoliza] = useState('');

  useEffect(() => {
    if (client?.id) {
      fetchMovimientos();
    }
  }, [client?.id]);

  const fetchMovimientos = async () => {
    setLoading(true);
    try {
        const endososRef = collectionGroup(db, 'endosos');
        const q = query(
          endososRef, 
          where('clienteId', '==', client.id),
          where('tipoGestion', '==', 'Cambio de Método de Pago')
        );
        
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        
        // Ordenamos por fecha de carga (más reciente primero)
        const sorted = data.sort((a, b) => new Date(b.fechaCarga) - new Date(a.fechaCarga));
        setMovimientos(sorted);
    } catch (err) {
        console.error("Error cargando historial de pagos:", err);
    } finally {
        setLoading(false);
    }
  };

  // Filtrado por número de póliza
  const movimientosFiltrados = useMemo(() => {
    if (!filtroPoliza) return movimientos;
    return movimientos.filter(m => 
      (m.nPoliza || '').toLowerCase().includes(filtroPoliza.toLowerCase())
    );
  }, [movimientos, filtroPoliza]);

  const formatDate = (dateString) => {
    if(!dateString) return '-';
    if (typeof dateString === 'string' && dateString.includes('-')) {
        const parts = dateString.split('-');
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateString;
  };

  const toggleSensitiveData = (e, id) => {
    e.stopPropagation();
    setShowSensitiveData(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // --- SUB-COMPONENTE: Ficha técnica del medio ---
  const PagoDetails = ({ mov }) => {
    const isCBU = String(mov.formaPago).toUpperCase() === 'CBU';
    const visible = showSensitiveData[mov.id];
    
    // Sincronización con nuevos campos y compatibilidad con anteriores
    const valorPrincipal = isCBU 
        ? (mov.numeroCbu || mov.cbu) 
        : (mov.numeroTarjeta || mov.numero || mov.nroTarjeta);

    const formatMasked = (val, type) => {
      if (!val) return '****';
      const stringVal = String(val).trim();
      if (visible) return stringVal;
      return type === 'CBU' 
        ? `**** **** **** ${stringVal.slice(-6)}` 
        : `**** **** **** ${stringVal.slice(-4)}`;
    };

    return (
      <div className="bg-white border border-blue-100 rounded-xl p-5 shadow-sm animate-fade-in text-left mb-4">
        <div className="flex items-center justify-between border-b border-gray-50 pb-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${isCBU ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
              {isCBU ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.6"><path d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.6"><path d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
              )}
            </div>
            <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Datos del Medio de Cobro</p>
                <p className="text-xs font-bold text-slate-600 mt-1 uppercase">{mov.formaPago}</p>
            </div>
          </div>
          <button 
            onClick={(e) => toggleSensitiveData(e, mov.id)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-all font-bold text-xs"
          >
            {visible ? (
              <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.6"><path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88L5.93 5.93m4.89 4.89l-1.12 1.12m0 0a3 3 0 004.242 4.243l3.025 3.025M12 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg> Ocultar</>
            ) : (
              <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.6"><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg> Ver Números</>
            )}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {isCBU ? (
            <>
              <div>
                <p className="text-[9px] font-black text-gray-400 uppercase">Banco</p>
                <p className="text-sm font-bold text-slate-800 uppercase">{mov.banco || '-'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-[9px] font-black text-gray-400 uppercase">Número de CBU</p>
                <p className={`text-sm font-mono font-black tracking-wider ${visible ? 'text-blue-700' : 'text-slate-500'}`}>
                  {formatMasked(valorPrincipal, 'CBU')}
                </p>
              </div>
            </>
          ) : (
            <>
              <div>
                <p className="text-[9px] font-black text-gray-400 uppercase">Marca / Banco</p>
                <p className="text-sm font-bold text-slate-800 uppercase">{mov.marca || 'S/D'} - {mov.banco || 'S/D'}</p>
              </div>
              <div>
                <p className="text-[9px] font-black text-gray-400 uppercase">Vencimiento</p>
                <p className="text-sm font-bold text-slate-800">{mov.vencimientoTarjeta || mov.vencimiento || '-'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-[9px] font-black text-gray-400 uppercase">Número de Tarjeta</p>
                <p className={`text-sm font-mono font-black tracking-wider ${visible ? 'text-blue-700' : 'text-slate-500'}`}>
                  {formatMasked(valorPrincipal, 'CARD')}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in text-left">
      
      {/* HEADER DE LA SOLAPA */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
            <h3 className="text-lg font-bold text-gray-800">Historial de Cambios de Cobro</h3>
            <p className="text-sm text-gray-500">Registro cronológico de medios de pago en todas sus pólizas.</p>
        </div>
        
        {/* BUSCADOR DE PÓLIZA */}
        <div className="w-full md:w-64 relative text-left">
          <input 
            type="text"
            placeholder="Buscar por N° Póliza..."
            value={filtroPoliza}
            onChange={(e) => setFiltroPoliza(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none shadow-sm text-left"
          />
          <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        </div>
      </div>

      {/* TABLA CON ESTÉTICA DE ENDOSOS */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-base text-gray-600 border-collapse">
            <thead className="bg-gray-50 text-gray-700 uppercase font-bold text-sm border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 w-10 text-center border-r border-gray-200">#</th> 
                <th className="px-4 py-3 border-r border-gray-200">Fecha de Carga</th>
                <th className="px-4 py-3 border-r border-gray-200">Póliza / Ramo</th>
                <th className="px-4 py-3 border-r border-gray-200">Nuevo Medio de Pago</th>
                <th className="px-4 py-3 border-r border-gray-200">Responsable de la Gestión</th>
                <th className="px-4 py-3 text-center">Estado Trámite</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="6" className="text-center py-12">Cargando movimientos...</td></tr>
              ) : movimientosFiltrados.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-12 text-gray-400 italic">No se encontraron movimientos registrados.</td></tr>
              ) : (
                movimientosFiltrados.map((mov, idx) => (
                  <React.Fragment key={mov.id}>
                    <tr 
                      className={`hover:bg-blue-50/30 transition-colors cursor-pointer ${expandedId === mov.id ? 'bg-blue-50/50' : ''}`}
                      onClick={() => setExpandedId(expandedId === mov.id ? null : mov.id)}
                    >
                      <td className="px-4 py-4 text-center border-r border-gray-100 text-gray-400">
                        <svg 
                          className={`w-5 h-5 transform transition-transform duration-200 ${expandedId === mov.id ? 'rotate-90 text-blue-600' : ''}`} 
                          fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                        </svg>
                      </td>
                      <td className="px-4 py-4 border-r border-gray-100 font-medium text-gray-900 tabular-nums">
                        {formatDate(mov.fechaCarga)}
                      </td>
                      <td className="px-4 py-4 border-r border-gray-100">
                        <div className="flex flex-col text-left">
                            <span className="text-blue-800 font-bold text-sm leading-tight">#{mov.nPoliza || 'PEND'}</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase">{mov.riesgo}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 border-r border-gray-100 text-left">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-bold border uppercase ${String(mov.formaPago).toUpperCase() === 'CBU' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-blue-50 text-blue-700 border-blue-100'}`}>
                            {mov.formaPago}
                        </span>
                      </td>
                      <td className="px-4 py-4 border-r border-gray-100 text-left">
                        {mov.quienGestiono || '-'}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs uppercase font-bold ${getEndosoStatusChip(mov.estadoGestion)}`}>
                          {mov.estadoGestion}
                        </span>
                      </td>
                    </tr>
                    
                    {/* Contenido Expandido */}
                    {expandedId === mov.id && (
                        <tr className="bg-gray-50 border-b border-gray-100 text-left">
                            <td colSpan="6" className="px-12 py-6">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start text-left">
                                    {/* Ficha Técnica */}
                                    <PagoDetails mov={mov} />
                                    
                                    {/* Notas y Metadata */}
                                    <div className="space-y-4 text-left">
                                        <div className="flex flex-col gap-1 text-left">
                                            <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Observaciones de la gestión:</h5>
                                            <div className="bg-white p-4 rounded-lg border border-gray-300 shadow-sm text-base text-gray-800 whitespace-pre-wrap leading-relaxed border-l-4 border-blue-200 italic text-left">
                                                {mov.observaciones || 'No hay notas adicionales.'}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 text-[11px] font-bold text-gray-600 uppercase tracking-widest pt-2">
                                            <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span> Trámite: {mov.tramite || 'S/D'}</div>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

    </div>
  );
};

export default MovimientosDePago;